/**
 * Phase 20 Plan 20-02 — AlertRow
 *
 * Single alert row for the NotificationsBell dropdown.
 * Shows severity icon, type label, message, relative time, and acknowledge button.
 *
 * Client feedback (2026-09): "notifications are coming without detail from the
 * notification icon". The row rendered `alert.message` inside a single
 * `truncate` line, so everything past the first clause was cut — and the
 * backend composer emits MULTI-LINE messages (`\nReason: …\nRemarks: …`), which
 * HTML collapsed to one line before truncation even applied. The row now shows
 * the notification type, preserves line breaks, clamps to three lines instead of
 * one, and opens the full detail dialog on click when `onSelect` is supplied.
 */

import { BellIcon, WarningCircleIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import type { JSX, KeyboardEvent, MouseEvent } from 'react';

import { Button } from '@/components/ui/button';
import { useAcknowledgeMutation } from '@/hooks/useNotificationsQueries';
import { notificationTypeLabel } from '@/lib/utils/notification-label';
import { formatRelative } from '@/lib/utils/relative-time';
import type { AlertDto } from '@/types/notifications';

interface AlertRowProps {
  readonly alert: AlertDto;
  readonly showAcknowledge?: boolean;
  /** When supplied the row becomes activatable and opens the alert detail dialog. */
  readonly onSelect?: (alert: AlertDto) => void;
}

export function AlertRow({ alert, showAcknowledge = false, onSelect }: AlertRowProps): JSX.Element {
  const ackMutation = useAcknowledgeMutation();

  const SeverityIcon =
    alert.severity === 'Critical'
      ? WarningOctagonIcon
      : alert.severity === 'Warning'
        ? WarningCircleIcon
        : BellIcon;

  const iconColor =
    alert.severity === 'Critical'
      ? 'text-destructive'
      : alert.severity === 'Warning'
        ? 'text-yellow-500'
        : 'text-muted-foreground';

  const handleAck = (e: MouseEvent<HTMLButtonElement>): void => {
    // Keep the click off the row so acknowledging never also opens the dialog.
    e.stopPropagation();
    ackMutation.mutate(alert.id);
  };

  const activate = (): void => {
    onSelect?.(alert);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (onSelect === undefined) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  return (
    <div
      className={`flex items-start gap-2 px-3 py-2 hover:bg-accent/50${
        onSelect !== undefined ? ' cursor-pointer' : ''
      }`}
      data-testid={`alert-row-${String(alert.id)}`}
      {...(onSelect !== undefined
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: activate,
            onKeyDown,
          }
        : {})}
    >
      <SeverityIcon size={16} weight="duotone" className={`mt-0.5 shrink-0 ${iconColor}`} />
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-xs font-medium"
          data-testid={`alert-row-type-${String(alert.id)}`}
        >
          {notificationTypeLabel(alert.notificationType)}
        </p>
        {/*
         * `whitespace-pre-line` keeps the composer's Reason/Remarks line breaks;
         * `line-clamp-3` bounds the row height without hiding the whole message
         * the way the old single-line `truncate` did. `title` exposes the full
         * text on hover for anything past three lines.
         */}
        <p
          className="line-clamp-3 whitespace-pre-line text-sm text-muted-foreground"
          title={alert.message}
          data-testid={`alert-row-message-${String(alert.id)}`}
        >
          {alert.message}
        </p>
        <p className="text-xs text-muted-foreground">{formatRelative(alert.createdAt)}</p>
      </div>
      {showAcknowledge && alert.status === 'New' && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleAck}
          disabled={ackMutation.isPending}
          data-testid={`alert-ack-btn-${String(alert.id)}`}
          className="shrink-0 text-xs"
        >
          Ack
        </Button>
      )}
    </div>
  );
}
